<template>
  <head>
    <meta charset="utf-8" />
    <title>Clearance Certificate Dashboard</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <link rel="shortcut icon" href="assets/images/favicon.svg">
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/custom.css" rel="stylesheet" type="text/css" />
  </head>

  <body data-sidebar="dark">
  <div id="layout-wrapper">
    <NavigationBar/>

    <div id="content" class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Welcome Header -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="welcome-card">
                <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div>
                    <h4 class="mb-1 fw-bold text-dark">Clearance Certificate Dashboard</h4>
                    <p class="text-muted mb-0">
                      <i class="mdi mdi-calendar-today me-1"></i>{{ todayDate }}
                      <span class="mx-2">•</span>
                      <span class="text-success"><i class="mdi mdi-circle-small"></i>Last updated: {{ lastUpdated }}</span>
                    </p>
                  </div>
                  <button @click="loadDashboardData" class="btn btn-primary d-flex align-items-center gap-2" :disabled="loading">
                    <i class="mdi mdi-refresh" :class="{'mdi-spin': loading}"></i>
                    Refresh Data
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Toolbar -->
          <ToolBarClearance/>

          <!-- Summary Statistics Cards -->
          <div class="row g-3 mb-4">
            <!-- Total Certificates -->
            <div class="col-xl-3 col-md-6">
              <div class="card stat-card h-100 border-0 shadow-sm">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="stat-icon bg-primary-subtle rounded-circle">
                      <i class="mdi mdi-file-certificate text-primary"></i>
                    </div>
                    <div class="ms-3 flex-grow-1">
                      <p class="text-muted mb-1 text-uppercase fw-semibold small">Total Certificates</p>
                      <h3 class="mb-0 fw-bold" v-if="!loading">{{ numberFormat(summary.total) }}</h3>
                      <div v-else class="placeholder-glow"><span class="placeholder col-6"></span></div>
                    </div>
                  </div>
                  <div class="mt-3 pt-3 border-top">
                    <router-link :to="{name: 'clearance-certificates'}" class="text-primary small fw-medium">
                      View All <i class="mdi mdi-arrow-right"></i>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pending -->
            <div class="col-xl-3 col-md-6">
              <div class="card stat-card h-100 border-0 shadow-sm border-start border-warning border-4">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="stat-icon bg-warning-subtle rounded-circle position-relative">
                      <i class="mdi mdi-clock-alert text-warning"></i>
                      <span v-if="summary.pending > 0" class="pulse-dot"></span>
                    </div>
                    <div class="ms-3 flex-grow-1">
                      <p class="text-muted mb-1 text-uppercase fw-semibold small">Pending Approval</p>
                      <h3 class="mb-0 fw-bold text-warning" v-if="!loading">{{ numberFormat(summary.pending) }}</h3>
                      <div v-else class="placeholder-glow"><span class="placeholder col-6"></span></div>
                    </div>
                  </div>
                  <div class="mt-3 pt-3 border-top">
                    <router-link :to="{name: 'clearance-pending'}" class="text-warning small fw-medium">
                      <span v-if="summary.pending > 0"><i class="mdi mdi-alert-circle"></i> Needs Action</span>
                      <span v-else><i class="mdi mdi-check-circle"></i> All Clear</span>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>

            <!-- Approved -->
            <div class="col-xl-3 col-md-6">
              <div class="card stat-card h-100 border-0 shadow-sm border-start border-success border-4">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="stat-icon bg-success-subtle rounded-circle">
                      <i class="mdi mdi-check-circle text-success"></i>
                    </div>
                    <div class="ms-3 flex-grow-1">
                      <p class="text-muted mb-1 text-uppercase fw-semibold small">Approved</p>
                      <h3 class="mb-0 fw-bold text-success" v-if="!loading">{{ numberFormat(summary.approved) }}</h3>
                      <div v-else class="placeholder-glow"><span class="placeholder col-6"></span></div>
                      <span class="badge bg-success-subtle text-success small mt-1">{{ approvalRate }}% Rate</span>
                    </div>
                  </div>
                  <div class="mt-3 pt-3 border-top">
                    <router-link :to="{name: 'clearance-approved'}" class="text-success small fw-medium">
                      View Approved <i class="mdi mdi-arrow-right"></i>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>

            <!-- Declined -->
            <div class="col-xl-3 col-md-6">
              <div class="card stat-card h-100 border-0 shadow-sm border-start border-danger border-4">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="stat-icon bg-danger-subtle rounded-circle">
                      <i class="mdi mdi-close-circle text-danger"></i>
                    </div>
                    <div class="ms-3 flex-grow-1">
                      <p class="text-muted mb-1 text-uppercase fw-semibold small">Declined</p>
                      <h3 class="mb-0 fw-bold text-danger" v-if="!loading">{{ numberFormat(summary.declined) }}</h3>
                      <div v-else class="placeholder-glow"><span class="placeholder col-6"></span></div>
                      <span class="badge bg-danger-subtle text-danger small mt-1">{{ declineRate }}% Rate</span>
                    </div>
                  </div>
                  <div class="mt-3 pt-3 border-top">
                    <router-link :to="{name: 'clearance-declined'}" class="text-danger small fw-medium">
                      View Declined <i class="mdi mdi-arrow-right"></i>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Main Content Row -->
          <div class="row g-3 mb-4">
            <!-- Revenue Card -->
            <div class="col-xl-4 col-lg-6">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-header bg-primary text-white py-3">
                  <div class="d-flex align-items-center">
                    <i class="mdi mdi-cash-multiple fs-4 me-2"></i>
                    <div>
                      <h6 class="mb-0 text-white">Revenue Overview</h6>
                      <small class="opacity-75">Certificate revenue statistics</small>
                    </div>
                  </div>
                </div>
                <div class="card-body">
                  <!-- Approved Revenue -->
                  <div class="revenue-item d-flex align-items-center p-3 rounded mb-3 bg-success-subtle">
                    <div class="revenue-icon bg-success text-white rounded-circle me-3">
                      <i class="mdi mdi-trending-up"></i>
                    </div>
                    <div class="flex-grow-1">
                      <small class="text-muted">Approved Revenue</small>
                      <h5 class="mb-0 text-success fw-bold">KES {{ numberFormat(revenue.approved) }}</h5>
                    </div>
                  </div>

                  <!-- Pending Revenue -->
                  <div class="revenue-item d-flex align-items-center p-3 rounded mb-3 bg-warning-subtle">
                    <div class="revenue-icon bg-warning text-white rounded-circle me-3">
                      <i class="mdi mdi-clock-outline"></i>
                    </div>
                    <div class="flex-grow-1">
                      <small class="text-muted">Pending Revenue</small>
                      <h5 class="mb-0 text-warning fw-bold">KES {{ numberFormat(revenue.pending) }}</h5>
                    </div>
                  </div>

                  <!-- This Month -->
                  <div class="revenue-item d-flex align-items-center p-3 rounded bg-primary-subtle">
                    <div class="revenue-icon bg-primary text-white rounded-circle me-3">
                      <i class="mdi mdi-calendar-month"></i>
                    </div>
                    <div class="flex-grow-1">
                      <small class="text-muted">This Month</small>
                      <h5 class="mb-0 text-primary fw-bold">KES {{ numberFormat(revenue.thisMonth) }}</h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Quick Actions -->
            <div class="col-xl-4 col-lg-6">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-header bg-info text-white py-3">
                  <div class="d-flex align-items-center">
                    <i class="mdi mdi-lightning-bolt fs-4 me-2"></i>
                    <div>
                      <h6 class="mb-0 text-white">Quick Actions</h6>
                      <small class="opacity-75">Navigate to key areas</small>
                    </div>
                  </div>
                </div>
                <div class="card-body">
                  <div class="d-grid gap-2">
                    <router-link :to="{name: 'clearance-pending'}" class="btn btn-outline-warning btn-lg d-flex align-items-center justify-content-between">
                      <span><i class="mdi mdi-clock-alert me-2"></i>Review Pending</span>
                      <span class="badge bg-warning text-dark">{{ summary.pending }}</span>
                    </router-link>

                    <router-link :to="{name: 'clearance-approved'}" class="btn btn-outline-success btn-lg d-flex align-items-center justify-content-between">
                      <span><i class="mdi mdi-check-circle me-2"></i>View Approved</span>
                      <span class="badge bg-success">{{ summary.approved }}</span>
                    </router-link>

                    <router-link :to="{name: 'clearance-declined'}" class="btn btn-outline-danger btn-lg d-flex align-items-center justify-content-between">
                      <span><i class="mdi mdi-close-circle me-2"></i>View Declined</span>
                      <span class="badge bg-danger">{{ summary.declined }}</span>
                    </router-link>

                    <router-link :to="{name: 'clearance-certificates'}" class="btn btn-outline-primary btn-lg d-flex align-items-center justify-content-between">
                      <span><i class="mdi mdi-view-list me-2"></i>View All</span>
                      <span class="badge bg-primary">{{ summary.total }}</span>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>

            <!-- Processing Statistics -->
            <div class="col-xl-4 col-lg-12">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-header bg-secondary text-white py-3">
                  <div class="d-flex align-items-center">
                    <i class="mdi mdi-chart-donut fs-4 me-2"></i>
                    <div>
                      <h6 class="mb-0 text-white">Processing Stats</h6>
                      <small class="opacity-75">Performance metrics</small>
                    </div>
                  </div>
                </div>
                <div class="card-body">
                  <!-- Progress Bars -->
                  <div class="mb-3">
                    <div class="d-flex justify-content-between mb-1">
                      <span class="small fw-medium">Approval Rate</span>
                      <span class="small text-success fw-bold">{{ approvalRate }}%</span>
                    </div>
                    <div class="progress" style="height: 8px;">
                      <div class="progress-bar bg-success" :style="{ width: approvalRate + '%' }"></div>
                    </div>
                  </div>

                  <div class="mb-3">
                    <div class="d-flex justify-content-between mb-1">
                      <span class="small fw-medium">Decline Rate</span>
                      <span class="small text-danger fw-bold">{{ declineRate }}%</span>
                    </div>
                    <div class="progress" style="height: 8px;">
                      <div class="progress-bar bg-danger" :style="{ width: declineRate + '%' }"></div>
                    </div>
                  </div>

                  <div class="mb-4">
                    <div class="d-flex justify-content-between mb-1">
                      <span class="small fw-medium">Pending</span>
                      <span class="small text-warning fw-bold">{{ pendingRate }}%</span>
                    </div>
                    <div class="progress" style="height: 8px;">
                      <div class="progress-bar bg-warning" :style="{ width: pendingRate + '%' }"></div>
                    </div>
                  </div>

                  <!-- Processing Numbers -->
                  <div class="row text-center g-0 border-top pt-3 mt-2">
                    <div class="col-4 border-end">
                      <h4 class="mb-1 text-primary fw-bold">{{ stats.todayProcessed }}</h4>
                      <small class="text-muted">Today</small>
                    </div>
                    <div class="col-4 border-end">
                      <h4 class="mb-1 text-info fw-bold">{{ stats.weekProcessed }}</h4>
                      <small class="text-muted">This Week</small>
                    </div>
                    <div class="col-4">
                      <h4 class="mb-1 text-secondary fw-bold">{{ stats.monthProcessed }}</h4>
                      <small class="text-muted">This Month</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Pending Certificates & Sub-County Stats -->
          <div class="row g-3 mb-4">
            <!-- Recent Pending -->
            <div class="col-xl-8">
              <div class="card border-0 shadow-sm">
                <div class="card-header bg-white py-3 d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <div class="icon-box bg-warning-subtle text-warning rounded me-3">
                      <i class="mdi mdi-clock-alert"></i>
                    </div>
                    <div>
                      <h6 class="mb-0 fw-bold">Recent Pending Certificates</h6>
                      <small class="text-muted">Awaiting your review</small>
                    </div>
                  </div>
                  <router-link :to="{name: 'clearance-pending'}" class="btn btn-sm btn-warning">
                    View All <i class="mdi mdi-arrow-right ms-1"></i>
                  </router-link>
                </div>
                <div class="card-body p-0">
                  <div class="table-responsive">
                    <table class="table table-hover mb-0">
                      <thead class="bg-light">
                      <tr>
                        <th class="border-0 ps-4">Bill No</th>
                        <th class="border-0">Customer</th>
                        <th class="border-0">Description</th>
                        <th class="border-0">Amount</th>
                        <th class="border-0">Date</th>

                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(item, index) in recentPending" :key="index">
                        <td class="ps-4">
                          <span class="fw-semibold text-primary">{{ item.billNo }}</span>
                        </td>
                        <td>
                          <div class="fw-medium">{{ item.names }}</div>
                          <small class="text-muted">ID: {{ item.idNo }}</small>
                        </td>
                        <td>
                          <span class="text-truncate d-inline-block" style="max-width: 150px;">
                            {{ item.feeDescription }}
                          </span>
                        </td>
                        <td>
                          <span class="fw-bold text-success">KES {{ numberFormat(item.billTotal) }}</span>
                        </td>
                        <td>
                          <small class="text-muted">{{ formatDate(item.dateCreated) }}</small>
                        </td>

                      </tr>
                      <tr v-if="recentPending.length === 0 && !loading">
                        <td colspan="6" class="text-center py-5">
                          <div class="empty-state">
                            <i class="mdi mdi-check-circle text-success" style="font-size: 48px;"></i>
                            <h6 class="mt-3 text-success">All Caught Up!</h6>
                            <p class="text-muted mb-0">No pending certificates to review</p>
                          </div>
                        </td>
                      </tr>
                      <tr v-if="loading">
                        <td colspan="6" class="text-center py-4">
                          <div class="spinner-border text-primary" role="status"></div>
                          <p class="text-muted mt-2 mb-0">Loading...</p>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <!-- Sub-County Breakdown -->
            <div class="col-xl-4">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-header bg-white py-3">
                  <div class="d-flex align-items-center">
                    <div class="icon-box bg-info-subtle text-info rounded me-3">
                      <i class="mdi mdi-map-marker"></i>
                    </div>
                    <div>
                      <h6 class="mb-0 fw-bold">By Sub-County</h6>
                      <small class="text-muted">Geographic distribution</small>
                    </div>
                  </div>
                </div>
                <div class="card-body p-0">
                  <div class="subcounty-list" style="max-height: 350px; overflow-y: auto;">
                    <div v-for="(item, index) in subCountyStats" :key="index" class="subcounty-item d-flex align-items-center justify-content-between px-4 py-3 border-bottom">
                      <div class="d-flex align-items-center">
                        <span class="badge bg-light text-dark me-2">{{ index + 1 }}</span>
                        <span class="fw-medium">{{ item.subCountyName }}</span>
                      </div>
                      <div class="d-flex gap-1">
                        <span class="badge bg-warning" title="Pending">{{ item.pending }}</span>
                        <span class="badge bg-success" title="Approved">{{ item.approved }}</span>
                        <span class="badge bg-primary" title="Total">{{ item.total }}</span>
                      </div>
                    </div>
                    <div v-if="subCountyStats.length === 0 && !loading" class="text-center py-5">
                      <i class="mdi mdi-map-marker-off text-muted" style="font-size: 36px;"></i>
                      <p class="text-muted mt-2 mb-0">No data available</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Recent Activity -->
          <div class="row g-3">
            <div class="col-12">
              <div class="card border-0 shadow-sm">
                <div class="card-header bg-white py-3 d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <div class="icon-box bg-primary-subtle text-primary rounded me-3">
                      <i class="mdi mdi-history"></i>
                    </div>
                    <div>
                      <h6 class="mb-0 fw-bold">Recent Activity</h6>
                      <small class="text-muted">Latest certificate actions</small>
                    </div>
                  </div>
                  <button @click="getRecentActivity" class="btn btn-sm btn-outline-primary" :disabled="loading">
                    <i class="mdi mdi-refresh" :class="{'mdi-spin': loading}"></i> Refresh
                  </button>
                </div>
                <div class="card-body">
                  <div class="row g-3" v-if="recentActivity.length > 0">
                    <div class="col-md-6 col-lg-4 col-xl-3" v-for="(activity, index) in recentActivity" :key="index">
                      <div class="activity-card p-3 rounded border h-100" :class="getActivityBorderClass(activity.approvalStatus)">
                        <div class="d-flex align-items-start">
                          <div class="activity-icon me-3">
                            <span class="rounded-circle d-flex align-items-center justify-content-center" :class="getActivityBgClass(activity.approvalStatus)" style="width: 40px; height: 40px;">
                              <i :class="getActivityIcon(activity.approvalStatus)"></i>
                            </span>
                          </div>
                          <div class="flex-grow-1 overflow-hidden">
                            <div class="d-flex justify-content-between align-items-center mb-1">
                              <h6 class="mb-0 text-truncate small fw-bold">{{ activity.billNo }}</h6>
                              <span class="badge" :class="getStatusBadgeClass(activity.approvalStatus)">
                                {{ activity.approvalStatus }}
                              </span>
                            </div>
                            <p class="text-muted mb-1 small text-truncate">{{ activity.names }}</p>
                            <div class="d-flex justify-content-between align-items-center">
                              <span class="fw-semibold text-success small">KES {{ numberFormat(activity.billTotal) }}</span>
                              <small class="text-muted">{{ formatTimeAgo(activity.approvedDate || activity.dateCreated) }}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-center py-5">
                    <i class="mdi mdi-history text-muted" style="font-size: 48px;"></i>
                    <h6 class="mt-3 text-muted">No Recent Activity</h6>
                    <p class="text-muted mb-0">Activity will appear once certificates are processed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Quick Decline Modal -->
      <div class="modal fade" id="quickDeclineModal" tabindex="-1">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content border-0">
            <div class="modal-header bg-danger text-white">
              <h5 class="modal-title text-white">
                <i class="mdi mdi-close-circle me-2"></i>Decline Certificate
              </h5>
              <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body" v-if="selectedCertificate">
              <div class="alert alert-light border mb-4">
                <div class="d-flex align-items-center">
                  <i class="mdi mdi-file-document text-danger fs-3 me-3"></i>
                  <div>
                    <h6 class="mb-1">{{ selectedCertificate.billNo }}</h6>
                    <p class="mb-0 text-muted small">{{ selectedCertificate.names }} • KES {{ numberFormat(selectedCertificate.billTotal) }}</p>
                  </div>
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label fw-semibold">Reason for Declining <span class="text-danger">*</span></label>
                <textarea v-model="declineReason" class="form-control" rows="4" placeholder="Please provide a detailed reason for declining this certificate..."></textarea>
              </div>
            </div>
            <div class="modal-footer border-0">
              <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
              <button @click="quickDecline" class="btn btn-danger" :disabled="processing || !declineReason.trim()">
                <span v-if="processing"><span class="spinner-border spinner-border-sm me-1"></span>Processing...</span>
                <span v-else><i class="mdi mdi-close me-1"></i>Decline</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6"></div>
          </div>
        </div>
      </footer>
    </div>
  </div>
  </body>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ToolBarClearance from "@/components/landrate/ToolBarClearance.vue";
import moment from "moment/moment";
import { execute, getRight, landRate } from "@/api";

export default {
  name: "ClearanceDashboard",
  components: { NavigationBar, ToolBarClearance },
  data() {
    return {
      loading: true,
      processing: false,
      lastUpdated: '',
      summary: {
        total: 0,
        pending: 0,
        approved: 0,
        declined: 0
      },
      revenue: {
        approved: 0,
        pending: 0,
        thisMonth: 0
      },
      stats: {
        todayProcessed: 0,
        weekProcessed: 0,
        monthProcessed: 0
      },
      recentPending: [],
      recentActivity: [],
      subCountyStats: [],
      selectedCertificate: null,
      declineReason: ''
    }
  },
  computed: {
    todayDate() {
      return moment().format('dddd, MMMM D, YYYY');
    },
    approvalRate() {
      const processed = this.summary.approved + this.summary.declined;
      if (processed === 0) return 0;
      return Math.round((this.summary.approved / processed) * 100);
    },
    declineRate() {
      const processed = this.summary.approved + this.summary.declined;
      if (processed === 0) return 0;
      return Math.round((this.summary.declined / processed) * 100);
    },
    pendingRate() {
      if (this.summary.total === 0) return 0;
      return Math.round((this.summary.pending / this.summary.total) * 100);
    }
  },
  mounted() {
    this.loadDashboardData();
  },
  methods: {
    getRight(type) { return getRight(type); },
    numberFormat(num) { return new Intl.NumberFormat("en-GB").format(num || 0); },
    formatDate(date) { return date ? moment(date).format('DD MMM YYYY') : ''; },
    formatDateTime(date) { return date ? moment(date).format('DD MMM YYYY HH:mm') : ''; },
    formatTimeAgo(date) { return date ? moment(date).fromNow() : ''; },

    getActivityBorderClass(status) {
      if (status === 'APPROVED') return 'border-success';
      if (status === 'DECLINED') return 'border-danger';
      return 'border-warning';
    },

    getActivityBgClass(status) {
      if (status === 'APPROVED') return 'bg-success text-white';
      if (status === 'DECLINED') return 'bg-danger text-white';
      return 'bg-warning text-dark';
    },

    getActivityIcon(status) {
      if (status === 'APPROVED') return 'mdi mdi-check';
      if (status === 'DECLINED') return 'mdi mdi-close';
      return 'mdi mdi-clock';
    },

    getStatusBadgeClass(status) {
      if (status === 'APPROVED') return 'bg-success';
      if (status === 'DECLINED') return 'bg-danger';
      return 'bg-warning text-dark';
    },

    async loadDashboardData() {
      this.loading = true;
      await Promise.all([
        this.getSummary(),
        this.getRevenue(),
        this.getProcessingStats(),
        this.getRecentPending(),
        this.getRecentActivity(),
        this.getSubCountyStats()
      ]);
      this.loading = false;
      this.lastUpdated = moment().format('HH:mm:ss');
    },

    getSummary() {
      const data = new FormData();
      data.append("function", "getClearanceCertificateSummary");

      return execute(data, landRate)
          .then((res) => {
            if (res.data.success) {
              this.summary = {
                ...res.data.data.summary,
                total: (res.data.data.summary.pending || 0) +
                    (res.data.data.summary.approved || 0) +
                    (res.data.data.summary.declined || 0)
              };
            }
          })
          .catch((e) => console.error(e));
    },

    getRevenue() {
      const data = new FormData();
      data.append("function", "getClearanceCertificateRevenue");

      return execute(data, landRate)
          .then((res) => {
            if (res.data.success) {
              this.revenue = res.data.data.revenue;
            }
          })
          .catch((e) => console.error(e));
    },

    getProcessingStats() {
      const data = new FormData();
      data.append("function", "getClearanceProcessingStats");

      return execute(data, landRate)
          .then((res) => {
            if (res.data.success) {
              this.stats = res.data.data.stats;
            }
          })
          .catch((e) => console.error(e));
    },

    getRecentPending() {
      const data = new FormData();
      data.append("function", "getClearanceCertificates");
      data.append("page", 1);
      data.append("rows_per_page", 5);
      data.append("approvalStatus", "PENDING");

      return execute(data, landRate)
          .then((res) => {
            if (res.data.success) {
              this.recentPending = res.data.data.certificates || [];
            }
          })
          .catch((e) => console.error(e));
    },

    getRecentActivity() {
      const data = new FormData();
      data.append("function", "getClearanceRecentActivity");
      data.append("limit", 8);

      return execute(data, landRate)
          .then((res) => {
            if (res.data.success) {
              this.recentActivity = res.data.data.activity || [];
            }
          })
          .catch((e) => console.error(e));
    },

    getSubCountyStats() {
      const data = new FormData();
      data.append("function", "getClearanceSubCountyStats");

      return execute(data, landRate)
          .then((res) => {
            if (res.data.success) {
              this.subCountyStats = res.data.data.subCountyStats || [];
            }
          })
          .catch((e) => console.error(e));
    },

    selectForDecline(item) {
      this.selectedCertificate = item;
      this.declineReason = '';
    },

    quickApprove(item) {
      if (!confirm(`Approve certificate ${item.billNo}?\n\nCustomer: ${item.names}\nAmount: KES ${this.numberFormat(item.billTotal)}`)) return;

      this.processing = true;
      const data = new FormData();
      data.append("function", "approveClearanceCertificate");
      data.append("id", item.id);
      data.append("billNo", item.billNo);
      data.append("notes", "Approved from dashboard");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, landRate)
          .then((res) => {
            this.processing = false;
            if (res.data.success) {
              alert("Certificate approved successfully!");
              this.loadDashboardData();
            } else {
              alert("Error: " + res.data.message);
            }
          })
          .catch((e) => {
            this.processing = false;
            alert("Error: " + e.message);
          });
    },

    quickDecline() {
      if (!this.selectedCertificate || !this.declineReason.trim()) {
        alert("Please provide a reason for declining.");
        return;
      }

      this.processing = true;
      const data = new FormData();
      data.append("function", "declineClearanceCertificate");
      data.append("id", this.selectedCertificate.id);
      data.append("billNo", this.selectedCertificate.billNo);
      data.append("declineReason", this.declineReason);
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, landRate)
          .then((res) => {
            this.processing = false;
            if (res.data.success) {
              alert("Certificate declined successfully!");
              this.closeModal('quickDeclineModal');
              this.loadDashboardData();
            } else {
              alert("Error: " + res.data.message);
            }
          })
          .catch((e) => {
            this.processing = false;
            alert("Error: " + e.message);
          });
    },

    closeModal(modalId) {
      const modal = document.getElementById(modalId);
      if (modal) {
        const modalInstance = bootstrap.Modal.getInstance(modal);
        if (modalInstance) modalInstance.hide();
      }
    }
  }
}
</script>

<style scoped>
/* Welcome Card */
.welcome-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
  padding: 1.25rem;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

/* Stat Cards */
.stat-card {
  border-radius: 12px;
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.1) !important;
}

.stat-icon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

/* Pulse Dot */
.pulse-dot {
  position: absolute;
  top: 0;
  right: 0;
  width: 12px;
  height: 12px;
  background: #f46a6a;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(244, 106, 106, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(244, 106, 106, 0); }
  100% { box-shadow: 0 0 0 0 rgba(244, 106, 106, 0); }
}

/* Icon Box */
.icon-box {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

/* Revenue Items */
.revenue-item {
  transition: all 0.2s ease;
}

.revenue-item:hover {
  transform: scale(1.02);
}

.revenue-icon {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

/* Sub-county List */
.subcounty-item {
  transition: background 0.2s ease;
}

.subcounty-item:hover {
  background: #f8f9fa;
}

/* Activity Card */
.activity-card {
  transition: all 0.2s ease;
  border-left-width: 3px !important;
}

.activity-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

/* Subtle Backgrounds */
.bg-primary-subtle { background-color: rgba(85, 110, 230, 0.15) !important; }
.bg-success-subtle { background-color: rgba(52, 195, 143, 0.15) !important; }
.bg-warning-subtle { background-color: rgba(241, 180, 76, 0.15) !important; }
.bg-danger-subtle { background-color: rgba(244, 106, 106, 0.15) !important; }
.bg-info-subtle { background-color: rgba(80, 165, 241, 0.15) !important; }

/* Border Colors */
.border-success { border-color: #34c38f !important; }
.border-danger { border-color: #f46a6a !important; }
.border-warning { border-color: #f1b44c !important; }

/* Custom Scrollbar */
.subcounty-list::-webkit-scrollbar {
  width: 5px;
}

.subcounty-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.subcounty-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

/* Table Hover */
.table-hover tbody tr:hover {
  background-color: rgba(85, 110, 230, 0.04);
}

/* Empty State */
.empty-state {
  padding: 1rem;
}

/* Card Headers */
.card-header {
  border-radius: 12px 12px 0 0 !important;
}

/* Responsive */
@media (max-width: 768px) {
  .welcome-card h4 {
    font-size: 1.1rem;
  }

  .stat-icon {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }
}
</style>